import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BrandAnchor,
  FormSection,
  Heading,
  LoginCard,
  LoginContainer,
  PasswordHeader,
  PasswordInputWrapper,
  PasswordLabel,
  PasswordToggleButton,
  SignInButton,
} from './Loging.styles';
import { Input } from '../../../components/uielements/input';
import { PageWrapper } from '../../landingPages/components/shared.styles';

import { ThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from '../../../settings/appStyle/theme';
import Navbar from '../../Navbar/Navbar';
import { login } from '../../../services/auth';

import { setSession } from '../../../services/service';

// ─── helpers ────────────────────────────────────────────────────────────────

const HelperText = ({ children }) => (
  <span
    role="alert"
    style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}
  >
    {children}
  </span>
);

const PasswordVisibilityToggle = ({ visible, onToggle }) => (
  <PasswordToggleButton
    type="button"
    onClick={onToggle}
    aria-label={visible ? 'Hide password' : 'Show password'}
    tabIndex={0}
  >
    {visible ? <EyeOff size={17} /> : <Eye size={17} />}
  </PasswordToggleButton>
);

const getJwtExpiryMs = (token) => {
  const FALLBACK_MS = Date.now() + 48 * 60 * 60 * 1000;

  try {
    const payloadSegment = token.split('.')[1];
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const { exp } = JSON.parse(json);
    return typeof exp === 'number' ? exp * 1000 : FALLBACK_MS;
  } catch (error) {
    console.error('Could not decode token expiry, falling back to 48h:', error);
    return FALLBACK_MS;
  }
};

// ─── Root router ────────────────────────────────────────────────────────────

const Login = () => {
  const [formState, setFormState] = useState('sign-in'); // 'sign-in' | 'sign-up' | 'forgot-password'

  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');

    if (savedTheme) {
      return savedTheme === 'dark';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <Navbar isDark={isDark} toggleTheme={toggleTheme} />
      <>
        <SignIn
          onGoSignUp={() => setFormState('sign-up')}
          onGoForgotPassword={() => setFormState('forgot-password')}
        />
      </>
    </ThemeProvider>
  );
};

export default Login;

// ─── Sign In ────────────────────────────────────────────────────────────────

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ password: '' });
  const [isLoading, setIsLoading] = useState(false);

  const redirectTo = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      setErrors({ password: 'Password is required.' });
      return;
    }

    setIsLoading(true);

    try {
      const res = await login(password);

      const payload = res?.data ?? res;

      if (payload?.token) {
        setSession({
          token: payload.token,
          expiry: getJwtExpiryMs(payload.token),
        });

        localStorage.setItem('token', 'true');

        navigate(redirectTo, { replace: true });
        return;
      }

      window.alert(payload?.message || 'Login failed. Please try again.');
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Incorrect password. Please try again.';
      window.alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageWrapper>
      <LoginContainer>
        <LoginCard>
          <BrandAnchor>
            <Heading>Sign In</Heading>
          </BrandAnchor>

          <FormSection onSubmit={handleSubmit}>
            <div style={{ width: '100%' }}>
              <PasswordHeader>
                <PasswordLabel htmlFor="password">Password</PasswordLabel>
              </PasswordHeader>
              <PasswordInputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((p) => ({ ...p, password: '' }));
                  }}
                  iconSize="13.333px"
                />
                <PasswordVisibilityToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              </PasswordInputWrapper>
              {errors.password && <HelperText>{errors.password}</HelperText>}
            </div>

            <SignInButton type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in…' : 'Sign In'}
            </SignInButton>
          </FormSection>
        </LoginCard>
      </LoginContainer>
    </PageWrapper>
  );
};
